import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { api } from '../services/api'
import { 
  Folder, FolderOpen, FileCode, FileText, File, 
  Plus, Trash2, RefreshCw, Search, ChevronRight, 
  ChevronDown, GitBranch, Settings, Database
} from 'lucide-react'
import { format } from 'date-fns'

interface Codebase {
  id: string
  name: string
  description?: string
  repoUrl?: string
  rootPath: string
  fileCount: number
  totalSize: number
  indexStatus: 'pending' | 'indexing' | 'complete' | 'error'
  lastIndexed?: string
  createdAt: string
}

interface FileNode {
  path: string
  name: string
  type: 'code' | 'text' | 'config' | 'binary'
  extension: string
  children?: FileNode[]
}

export default function CodebasesPage() {
  const { user, accessToken } = useAuthStore()
  const [codebases, setCodebases] = useState<Codebase[]>([])
  const [selectedCodebase, setSelectedCodebase] = useState<Codebase | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isIndexing, setIsIndexing] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  // New codebase form
  const [newCodebase, setNewCodebase] = useState({
    name: '',
    rootPath: '/path/to/project',
    repoUrl: '',
    description: ''
  })

  useEffect(() => {
    fetchCodebases()
  }, [user])

  const fetchCodebases = async () => {
    if (!accessToken) return
    setIsLoading(true)
    try {
      const response = await api.get('/codebases')
      setCodebases(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch codebases:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateCodebase = async () => {
    try {
      const response = await api.post('/codebases', newCodebase)
      setCodebases([response.data.data, ...codebases])
      setShowAddModal(false)
      setNewCodebase({ name: '', rootPath: '/path/to/project', repoUrl: '', description: '' })
    } catch (error) {
      console.error('Failed to create codebase:', error)
    }
  }

  const handleIndexCodebase = async (codebaseId: string) => {
    setIsIndexing(true)
    try {
      await api.post(`/codebases/${codebaseId}/index`)
      await fetchCodebases()
    } catch (error) {
      console.error('Failed to index codebase:', error)
    } finally {
      setIsIndexing(false)
    }
  }

  const handleDeleteCodebase = async (codebaseId: string) => {
    if (!confirm('Are you sure you want to delete this codebase?')) return
    try {
      await api.delete(`/codebases/${codebaseId}`)
      setCodebases(codebases.filter(c => c.id !== codebaseId))
      if (selectedCodebase?.id === codebaseId) {
        setSelectedCodebase(null)
      }
    } catch (error) {
      console.error('Failed to delete codebase:', error)
    }
  }

  const toggleFolder = (path: string) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(path)) {
      newExpanded.delete(path)
    } else {
      newExpanded.add(path)
    }
    setExpandedFolders(newExpanded)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'text-green-400'
      case 'indexing': return 'text-yellow-400'
      case 'error': return 'text-red-400'
      default: return 'text-dark-400'
    }
  }

  const getFileIcon = (extension: string, type: string) => {
    if (type === 'config') return <Settings className="w-4 h-4 text-yellow-400" />
    if (type === 'text') return <FileText className="w-4 h-4 text-blue-400" />
    if (type === 'binary') return <File className="w-4 h-4 text-dark-400" />
    return <FileCode className="w-4 h-4 text-primary-400" />
  }

  return (
    <div className="h-full flex bg-dark-900">
      {/* Sidebar - Codebase List */}
      <div className="w-80 border-r border-dark-700 flex flex-col">
        <div className="p-4 border-b border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Codebases</h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="p-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
            <input
              type="text"
              placeholder="Search codebases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-dark-800 border border-dark-600 rounded-lg pl-9 pr-3 py-2 text-sm text-dark-200 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Codebase List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-dark-400">Loading...</div>
          ) : codebases.length === 0 ? (
            <div className="p-8 text-center">
              <Database className="w-12 h-12 text-dark-600 mx-auto mb-3" />
              <p className="text-dark-400 mb-2">No codebases yet</p>
              <p className="text-sm text-dark-500">Add a repository to enable codebase indexing and context awareness.</p>
            </div>
          ) : (
            <div className="p-2">
              {codebases
                .filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map(codebase => (
                  <div
                    key={codebase.id}
                    onClick={() => setSelectedCodebase(codebase)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors mb-1 ${
                      selectedCodebase?.id === codebase.id 
                        ? 'bg-dark-700 border border-primary-500/30' 
                        : 'hover:bg-dark-800'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Folder className={`w-5 h-5 ${
                        selectedCodebase?.id === codebase.id ? 'text-primary-400' : 'text-yellow-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-dark-200 truncate">{codebase.name}</p>
                        <div className="flex items-center gap-2 text-xs text-dark-500">
                          <span>{codebase.fileCount} files</span>
                          <span>•</span>
                          <span className={getStatusColor(codebase.indexStatus)}>
                            {codebase.indexStatus}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {selectedCodebase ? (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <div className="flex items-center gap-4">
                <FolderOpen className="w-6 h-6 text-primary-400" />
                <div>
                  <h2 className="text-xl font-semibold text-white">{selectedCodebase.name}</h2>
                  <p className="text-sm text-dark-400">{selectedCodebase.rootPath}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleIndexCodebase(selectedCodebase.id)}
                  disabled={isIndexing || selectedCodebase.indexStatus === 'indexing'}
                  className="flex items-center gap-2 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isIndexing ? 'animate-spin' : ''}`} />
                  {isIndexing ? 'Indexing...' : 'Re-index'}
                </button>
                <button
                  onClick={() => handleDeleteCodebase(selectedCodebase.id)}
                  className="p-2 text-dark-400 hover:text-red-400 hover:bg-dark-700 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="flex items-center gap-6 px-4 py-2 bg-dark-800 border-b border-dark-700 text-sm">
              <div className="flex items-center gap-2 text-dark-300">
                <Database className="w-4 h-4 text-dark-400" />
                <span>{selectedCodebase.fileCount} files</span>
              </div>
              <div className="flex items-center gap-2 text-dark-300">
                <FileCode className="w-4 h-4 text-dark-400" />
                <span>{formatFileSize(selectedCodebase.totalSize)}</span>
              </div>
              <div className="flex items-center gap-2 text-dark-300">
                <GitBranch className="w-4 h-4 text-dark-400" />
                <span className={getStatusColor(selectedCodebase.indexStatus)}>
                  {selectedCodebase.indexStatus}
                </span>
              </div>
              {selectedCodebase.lastIndexed && (
                <div className="ml-auto text-dark-400">
                  Last indexed: {format(new Date(selectedCodebase.lastIndexed), 'MMM d, yyyy HH:mm')}
                </div>
              )}
            </div>

            {/* File Tree / Welcome */}
            <div className="flex-1 overflow-y-auto p-4">
              {selectedCodebase.indexStatus === 'complete' ? (
                <div className="card">
                  <div className="p-4 border-b border-dark-700">
                    <h3 className="text-lg font-medium text-white mb-2">Project Structure</h3>
                    <p className="text-sm text-dark-400">
                      The codebase has been indexed and is ready to provide context for AI conversations.
                    </p>
                  </div>
                  <div className="p-4">
                    <pre className="text-sm text-dark-300 font-mono whitespace-pre-wrap">
{`📁 ${selectedCodebase.name}/
├── src/
│   ├── components/
│   ├── pages/
│   ├── services/
│   └── stores/
├── backend/
│   ├── src/
│   └── prisma/
└── ...`}
                    </pre>
                  </div>
                </div>
              ) : selectedCodebase.indexStatus === 'indexing' ? (
                <div className="card text-center py-12">
                  <RefreshCw className="w-12 h-12 text-primary-400 animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Indexing Codebase</h3>
                  <p className="text-dark-400">Scanning files and building context...</p>
                </div>
              ) : (
                <div className="card text-center py-12">
                  <Database className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Codebase Not Indexed</h3>
                  <p className="text-dark-400 mb-4">
                    Click "Re-index" to scan files and enable context awareness.
                  </p>
                  <button
                    onClick={() => handleIndexCodebase(selectedCodebase.id)}
                    className="btn-primary"
                  >
                    Start Indexing
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Folder className="w-16 h-16 text-dark-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">Select a Codebase</h3>
              <p className="text-dark-400">Choose a codebase from the sidebar or create a new one.</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Codebase Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Add Codebase</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-200 mb-1">Name</label>
                <input
                  type="text"
                  value={newCodebase.name}
                  onChange={(e) => setNewCodebase({ ...newCodebase, name: e.target.value })}
                  placeholder="My Project"
                  className="input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-200 mb-1">Root Path</label>
                <input
                  type="text"
                  value={newCodebase.rootPath}
                  onChange={(e) => setNewCodebase({ ...newCodebase, rootPath: e.target.value })}
                  placeholder="/path/to/project"
                  className="input"
                />
                <p className="text-xs text-dark-500 mt-1">Absolute path to the project root directory</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-200 mb-1">Repository URL (optional)</label>
                <input
                  type="text"
                  value={newCodebase.repoUrl}
                  onChange={(e) => setNewCodebase({ ...newCodebase, repoUrl: e.target.value })}
                  placeholder="https://github.com/user/repo"
                  className="input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-200 mb-1">Description (optional)</label>
                <textarea
                  value={newCodebase.description}
                  onChange={(e) => setNewCodebase({ ...newCodebase, description: e.target.value })}
                  placeholder="Brief description..."
                  className="input resize-none"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-dark-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCodebase}
                disabled={!newCodebase.name || !newCodebase.rootPath}
                className="btn-primary disabled:opacity-50"
              >
                Add Codebase
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
